import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { CheckIcon } from '@change/ds-icons/CheckIcon';
import { LinkIcon } from '@change/ds-icons/LinkIcon';
import { tv } from '@change/ds-utils';

import { type TranslatableLabel, useResolvedLabel } from '#src/app/shared/hooks/i18n';
import type { ShareAction as ShareActionType, ShareChannel } from '#src/app/shared/hooks/share';

import { useCopyLinkVisibleUrlButton } from './hooks/useCopyLinkVisibleUrlButton';

type SupportedShareChannels = ShareChannel;
type ShareButtonProps = Readonly<{
	buttonConfig: {
		ShareAction: ShareActionType['ShareAction'];
		channel: SupportedShareChannels;
		label: TranslatableLabel;
		successLabel?: TranslatableLabel;
		url: string;
		urlDescription?: TranslatableLabel;
	};
}> &
	React.ComponentPropsWithoutRef<'div'>;

export const CopyLinkVisibleUrlButton: React.FC<ShareButtonProps> = ({
	buttonConfig: { ShareAction, label, successLabel, url, urlDescription },
	...rest
}) => {
	const { data, actions } = useCopyLinkVisibleUrlButton({ label, successLabel, url });
	const buttonLabel = useResolvedLabel(data.buttonLabel);
	const resolvedDescription = useResolvedLabel(urlDescription);

	const buttonClass = tv({
		base: 'flex',
		variants: {
			clicked: {
				true: 'text-typography-success',
				false: 'text-icon-strong-fixed',
			},
		},
	});

	return (
		<div className="border-stroke-weaker elevation-low flex flex-col gap-3 rounded-lg border px-5 py-4" {...rest}>
			<div className="bg-fill-weaker rounded-md px-7 py-3 text-xs">
				<div className="text-typography-strong leading-5">{resolvedDescription}</div>
				<div className="text-typography-strong overflow-hidden text-xs leading-6 font-bold text-ellipsis whitespace-nowrap select-none">
					{data.resolvedUrl}
				</div>
			</div>

			<ShareAction onAfterAction={actions.afterShare}>
				<Button
					leadingIcon={
						data.clickedState ? <CheckIcon data-qa="copied-icon-check" className="text-icon-success" /> : <LinkIcon />
					}
					variant="primary"
					disabled={data.clickedState}
					className={buttonClass({ clicked: data.clickedState })}
				>
					<Text size="medium" fontWeight="bold" className="flex gap-1.5" color="bg-red-500">
						{buttonLabel}
					</Text>
				</Button>
			</ShareAction>
		</div>
	);
};
